import {PlayArrow, Refresh} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {useEffect, useRef} from 'react';
import {QuizSession, QuizTimedMode} from '@cp/model';
import {env} from '../../config/env';

interface SessionResumeDialogProps {
  open: boolean;
  onClose: () => void;
  activeSession: QuizSession;
  onResume: (sessionId: string) => void;
  onStartNew: () => void;
}

export default function SessionResumeDialog({
  open,
  onClose,
  activeSession,
  onResume,
  onStartNew,
}: SessionResumeDialogProps) {
  const getSessionModeLabel = (mode: QuizTimedMode) => {
    switch (mode) {
      case QuizTimedMode.PRACTICE:
        return 'Practice';
      case QuizTimedMode.TIMED:
        return 'Timed';
      case QuizTimedMode.TIMED_PER_QUESTION:
        return 'Timed per Question';
      default:
        return 'Unknown';
    }
  };
  const getProgressInfo = () => {
    const totalQuestions = activeSession.questions.length;
    const answeredQuestions = activeSession.questions.filter((q) => q.selectedAnswer).length;
    const progress = Math.round((answeredQuestions / totalQuestions) * 100);

    return {
      answered: answeredQuestions,
      total: totalQuestions,
      progress,
    };
  };

  const progressInfo = getProgressInfo();
  const resumeButtonRef = useRef<HTMLButtonElement>(null);

  // Move focus into the dialog when it opens so the backdrop (e.g. topic tree checkbox) is not
  // focusable while aria-hidden, which would trigger an a11y violation.
  useEffect(() => {
    if (open && resumeButtonRef.current) {
      resumeButtonRef.current.focus();
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{fontWeight: 600}}>Active Session Found</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{mt: 1}}>
          <Alert severity="info">
            You have an active study session for this module. You can resume it or start a new one.
          </Alert>

          {/* Active Session Info */}
          <Paper sx={{p: 3, bgcolor: 'grey.50'}}>
            <Typography variant="h6" sx={{fontWeight: 600, mb: 2}}>
              Current Session
            </Typography>

            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: {xs: 'column', sm: 'row'},
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Progress:
                </Typography>
                <Chip
                  label={`${progressInfo.answered}/${progressInfo.total} questions`}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: {xs: 'column', sm: 'row'},
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Difficulty:
                </Typography>
                <Chip label={activeSession.difficulty || env.label.levelAuto} color="secondary" variant="outlined" />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: {xs: 'column', sm: 'row'},
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Mode:
                </Typography>
                <Chip label={getSessionModeLabel(activeSession.timedMode)} color="primary" variant="outlined" />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexDirection: {xs: 'column', sm: 'row'},
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Started:
                </Typography>
                <Typography variant="body2">
                  {activeSession.startedAt ? new Date(activeSession.startedAt).toLocaleString() : 'Unknown'}
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Progress Bar */}
          <Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
              <Typography variant="body2" sx={{fontWeight: 600}}>
                Session Progress
              </Typography>
              <Typography variant="body2" color="primary">
                {progressInfo.progress}%
              </Typography>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 8,
                bgcolor: 'grey.200',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${progressInfo.progress}%`,
                  height: '100%',
                  bgcolor: 'primary.main',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>

          {/* Warning */}
          <Alert severity="warning">
            <Typography variant="body2">
              <strong>Note:</strong> Starting a new session will close your current active session. Your progress will
              be saved, but you won&apos;t be able to continue from where you left off.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{p: 3, pt: 1}}>
        <Stack
          spacing={2}
          direction={{xs: 'column-reverse', sm: 'row'}}
          sx={{
            width: '100%',
            pt: 2,
            justifyContent: {xs: 'flex-start', sm: 'flex-end'},
          }}
        >
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={() => onStartNew()} variant="outlined" color="warning" startIcon={<Refresh />}>
            Start New Session
          </Button>
          <Button
            ref={resumeButtonRef}
            autoFocus
            onClick={() => onResume(activeSession.id)}
            variant="contained"
            startIcon={<PlayArrow />}
          >
            Resume Session
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
